// Category detection for workouts - Milestone 3
// Simple keyword-based tagging

import type { WorkoutEntry } from '../types/workout';

export type WorkoutCategory = 'Push' | 'Pull' | 'Legs' | 'Core' | 'Run';

const CATEGORY_KEYWORDS: Record<WorkoutCategory, string[]> = {
  Push: [
    'bench',
    'chest press',
    'shoulder press',
    'overhead press',
    'pec deck',
    'chest fly',
  ],
  Pull: [
    'row',
    'seated row',
    'cable row',
    'lat pulldown',
    'pull-up',
    'pullup',
    'face pull',
    'reverse fly',
  ],
  Legs: [
    'leg press',
    'lunge',
    'lunges',
    'leg curl',
    'hamstring curl',
    'leg extension',
    'squat',
    'squats',
  ],
  Core: [
    'plank',
    'dead bug',
    'bird dog',
    'core',
    'glute bridge',
  ],
  Run: [
    'run',
    'running',
    'jog',
    'trail',
    'intervals',
    'tempo',
    'treadmill',
    'parkrun',
  ],
};

/**
 * Detect workout categories from text and workout type
 * Returns an array of categories (can have multiple)
 */
export function detectWorkoutCategories(
  text: string | undefined,
  workoutType?: WorkoutEntry['type'],
): WorkoutCategory[] {
  const categories = new Set<WorkoutCategory>();
  
  // Normalize text for searching
  const searchText = text ? text.toLowerCase() : '';
  
  // Check for Run category
  if (workoutType === 'run') {
    categories.add('Run');
  } else if (searchText) {
    // Check Run keywords even if type isn't "run"
    for (const keyword of CATEGORY_KEYWORDS.Run) {
      if (searchText.includes(keyword.toLowerCase())) {
        categories.add('Run');
        break;
      }
    }
  }
  
  // Check other categories
  for (const [category, keywords] of Object.entries(CATEGORY_KEYWORDS)) {
    // Skip Run as we already handled it
    if (category === 'Run') continue;
    
    for (const keyword of keywords) {
      if (searchText.includes(keyword.toLowerCase())) {
        categories.add(category as WorkoutCategory);
        break; // Found a match for this category, move to next
      }
    }
  }
  
  return Array.from(categories);
}

/**
 * Detect categories from a workout entry
 * Checks description, exercises, and pastedText
 */
export function detectCategoriesFromWorkout(workout: {
  description?: string;
  exercises?: string;
  pastedText?: string;
  type?: WorkoutEntry['type'];
}): WorkoutCategory[] {
  // Combine all text sources
  const allText = [
    workout.description,
    workout.exercises,
    workout.pastedText,
  ]
    .filter(Boolean)
    .join(' ');
  
  return detectWorkoutCategories(allText || undefined, workout.type);
}

